import "./Stepper.css";
import { getStepVisualState, isConnectorFilled, isStepClickable } from "./stepperUtils";

export type StepperStep<TId extends string = string> = { id: TId; label: string };

export type StepperProps<TId extends string = string> = {
    steps: StepperStep<TId>[];
    currentIndex: number;
    furthestIndex: number;
    maxReachableIndex: number;
    onStepClick: (id: TId) => void;
    ariaLabel?: string;
    className?: string;
};

export function Stepper<TId extends string = string>({
    steps,
    currentIndex,
    furthestIndex,
    maxReachableIndex,
    onStepClick,
    ariaLabel = "Progress",
    className,
}: StepperProps<TId>) {
    if (steps.length === 0) {
        return null;
    }

    const rootClassName = ["journey-stepper", "mb-4", className].filter(Boolean).join(" ");

    return (
        <nav className={rootClassName} aria-label={ariaLabel}>
            <ol className="journey-stepper__list">
                {steps.map((step, index) => {
                    const visualState = getStepVisualState({
                        index,
                        currentIndex,
                        furthestIndex,
                        maxReachableIndex,
                    });
                    const clickable = isStepClickable(visualState);
                    const connectorFilled = index < steps.length - 1 && isConnectorFilled(index + 1, furthestIndex);

                    const stepContent = (
                        <>
                            <span className="journey-stepper__circle" aria-hidden="true">
                                {index + 1}
                            </span>
                            <span className="journey-stepper__label">{step.label}</span>
                        </>
                    );

                    return (
                        <li key={step.id} className="journey-stepper__item">
                            {clickable ? (
                                <button
                                    type="button"
                                    className="journey-stepper__step journey-stepper__step--interactive"
                                    data-state={visualState}
                                    onClick={() => onStepClick(step.id)}
                                >
                                    {stepContent}
                                </button>
                            ) : (
                                <span
                                    className="journey-stepper__step journey-stepper__step--static"
                                    data-state={visualState}
                                    aria-current={visualState === "current" ? "step" : undefined}
                                    aria-disabled={
                                        visualState === "upcoming" || visualState === "locked" ? true : undefined
                                    }
                                    tabIndex={-1}
                                >
                                    {stepContent}
                                </span>
                            )}
                            {index < steps.length - 1 && (
                                <div className="journey-stepper__connector" aria-hidden="true">
                                    <div className="journey-stepper__connector-track" />
                                    <div
                                        className={[
                                            "journey-stepper__connector-fill",
                                            connectorFilled ? "journey-stepper__connector-fill--filled" : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    />
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
